import { supabase } from "@/integrations/supabase/client";

export type McpField = {
  name: string;
  label: string;
  type: "string" | "text" | "number" | "boolean" | "uuid";
  required?: boolean;
  placeholder?: string;
  description?: string;
};

export type McpToolDef = {
  name: string;
  title: string;
  description: string;
  kind: "read" | "write";
  fields: McpField[];
  samplePrompts: string[];
  run: (input: Record<string, any>) => Promise<{ ok: boolean; data?: any; error?: string }>;
};

const toolListRegions: McpToolDef = {
  name: "list_regions",
  title: "List OFUESI regions",
  description: "Lists all OFUESI regions with their names and IDs.",
  kind: "read",
  fields: [],
  samplePrompts: [
    "List all OFUESI regions.",
    "What regions does OFUESI have?",
  ],
  run: async () => {
    const { data, error } = await supabase.from("regions").select("id, name").order("name");
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: { regions: data ?? [] } };
  },
};

const toolListConferences: McpToolDef = {
  name: "list_conferences",
  title: "List OFUESI conferences",
  description: "Lists active OFUESI conferences with name, region, date, and location.",
  kind: "read",
  fields: [
    { name: "limit", label: "Limit", type: "number", placeholder: "20", description: "Max rows (1–100)." },
  ],
  samplePrompts: [
    "List the 5 most recent OFUESI conferences.",
    "Show me active conferences and their locations.",
  ],
  run: async (input) => {
    const limit = Math.max(1, Math.min(100, Number(input.limit) || 20));
    const { data, error } = await supabase
      .from("conferences")
      .select("id, conference_name, region_id, conference_date, location, status, is_active")
      .eq("is_active", true)
      .order("conference_date", { ascending: false })
      .limit(limit);
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: { conferences: data ?? [] } };
  },
};

const toolListUniversities: McpToolDef = {
  name: "list_universities",
  title: "List OFUESI universities",
  description: "Lists universities served by OFUESI along with the region each belongs to.",
  kind: "read",
  fields: [
    { name: "region", label: "Region filter", type: "string", placeholder: "e.g. Southern" },
    { name: "search", label: "Search name", type: "string", placeholder: "e.g. Georgia" },
    { name: "limit", label: "Limit", type: "number", placeholder: "200" },
  ],
  samplePrompts: [
    "List all universities in the Southern region.",
    "Find universities matching 'state' in their name.",
  ],
  run: async (input) => {
    const limit = Math.max(1, Math.min(500, Number(input.limit) || 200));
    let query = supabase
      .from("universities")
      .select("id, name, region_id, regions:region_id ( name )")
      .order("name")
      .limit(limit);
    if (input.search) query = query.ilike("name", `%${input.search}%`);
    const { data, error } = await query;
    if (error) return { ok: false, error: error.message };
    const rows = (data ?? [])
      .map((u: any) => ({ id: u.id, name: u.name, region: u.regions?.name ?? null, region_id: u.region_id }))
      .filter((u) => (input.region ? (u.region ?? "").toLowerCase().includes(String(input.region).toLowerCase()) : true));
    return { ok: true, data: { universities: rows } };
  },
};

const toolListUpcomingEvents: McpToolDef = {
  name: "list_upcoming_events",
  title: "List upcoming OFUESI events",
  description: "Lists upcoming events with dates, locations, and links (flyer, registration, live stream).",
  kind: "read",
  fields: [
    { name: "region", label: "Region filter", type: "string", placeholder: "e.g. Southern" },
    { name: "limit", label: "Limit", type: "number", placeholder: "20" },
  ],
  samplePrompts: [
    "What OFUESI events are coming up in the next month?",
    "Show me upcoming events in the Southern region with their registration links.",
  ],
  run: async (input) => {
    const limit = Math.max(1, Math.min(100, Number(input.limit) || 20));
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from("events")
      .select(
        "id, title, description, event_date, location, category, theme, status, flyer_url, registration_url, live_url, region_id, regions:region_id ( name )"
      )
      .gte("event_date", nowIso)
      .order("event_date", { ascending: true })
      .limit(limit);
    if (error) return { ok: false, error: error.message };
    const rows = (data ?? [])
      .map((e: any) => ({
        id: e.id,
        title: e.title,
        description: e.description,
        event_date: e.event_date,
        location: e.location,
        category: e.category,
        theme: e.theme,
        status: e.status,
        region: e.regions?.name ?? null,
        links: { flyer: e.flyer_url ?? null, registration: e.registration_url ?? null, live: e.live_url ?? null },
      }))
      .filter((e) => (input.region ? (e.region ?? "").toLowerCase().includes(String(input.region).toLowerCase()) : true));
    return { ok: true, data: { events: rows } };
  },
};

const toolSubmitPrayerRequest: McpToolDef = {
  name: "submit_prayer_request",
  title: "Submit a prayer request",
  description:
    "Submits a prayer request to OFUESI and returns a confirmation ID. Set 'is_anonymous' to hide the requester's name and email from public views.",
  kind: "write",
  fields: [
    { name: "title", label: "Title", type: "string", required: true, placeholder: "Short title (3–200 chars)" },
    { name: "description", label: "Description", type: "text", placeholder: "Details (up to 4000 chars)" },
    { name: "requester_name", label: "Requester name", type: "string", placeholder: "Optional" },
    { name: "requester_email", label: "Requester email", type: "string", placeholder: "Optional" },
    { name: "is_anonymous", label: "Anonymous", type: "boolean", description: "Hide requester identity from public views." },
    { name: "region_id", label: "Region ID", type: "uuid", placeholder: "Optional UUID (use list_regions)" },
  ],
  samplePrompts: [
    "Submit an anonymous prayer request titled 'Wisdom for exams' asking for prayers for university students during finals.",
    "Submit a prayer request from 'Jane Doe' (jane@example.com) titled 'Job search' — she needs prayer for guidance in a new role.",
  ],
  run: async (input) => {
    const title = String(input.title ?? "").trim();
    if (title.length < 3 || title.length > 200) {
      return { ok: false, error: "Title must be 3–200 characters." };
    }
    const isAnon = Boolean(input.is_anonymous);
    const payload: Record<string, any> = {
      title,
      description: input.description ? String(input.description).slice(0, 4000) : null,
      requester_name: isAnon ? null : input.requester_name || null,
      requester_email: isAnon ? null : input.requester_email || null,
      is_anonymous: isAnon,
      region_id: input.region_id || null,
      status: "pending",
    };
    const { data, error } = await supabase
      .from("prayer_requests")
      .insert(payload)
      .select("id, created_at")
      .single();
    if (error || !data) return { ok: false, error: error?.message ?? "Failed to submit prayer request" };
    return { ok: true, data: { confirmation_id: data.id, created_at: data.created_at } };
  },
};

export const MCP_TOOLS: McpToolDef[] = [
  toolListRegions,
  toolListConferences,
  toolListUniversities,
  toolListUpcomingEvents,
  toolSubmitPrayerRequest,
];
